// Medication Administration Record (MAR) panel — Phase 4 (2026-10-04).
//
// Rendered as a modal from an admission card. Lists the admission's live
// prescriptions, each with a per-dose "Give / Hold / Miss / Refuse"
// quick-action and an administration history. Nurse-only in the role
// matrix; the API also gates (mar.administer).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    MEDICATION_ADMINISTRATION_STATUS_LABELS,
    type MarRequestLine,
    type MedicationAdministrationStatus,
} from '@pamosi/shared';
import { ApiError, admissionsApi } from '../lib/api';
import { ModalShell } from './AdmitDialog';

interface Props {
    admissionId: string;
    onClose: () => void;
}

const STATUSES: readonly MedicationAdministrationStatus[] = [
    'given', 'held', 'missed', 'refused',
];

export function MarPanel({ admissionId, onClose }: Props) {
    const q = useQuery({
        queryKey: ['admissions', admissionId, 'mar'],
        queryFn: () => admissionsApi.mar(admissionId),
    });

    return (
        <ModalShell title="Medication administration record" onClose={onClose}>
            {q.isLoading && <div className="text-slate-500 text-sm">Loading MAR…</div>}
            {q.isError && (
                <div className="pm-error text-sm">
                    {q.error instanceof ApiError ? q.error.message : 'Could not load MAR.'}
                </div>
            )}
            {q.data && q.data.lines.length === 0 && (
                <div className="text-slate-500 text-sm italic">
                    No prescriptions on this admission's encounter yet.
                </div>
            )}
            {q.data && q.data.lines.length > 0 && (
                <ul className="space-y-3 max-h-[70vh] overflow-y-auto">
                    {q.data.lines.map((line) => (
                        <MarLineRow
                            key={line.medication_request_id}
                            admissionId={admissionId}
                            line={line}
                        />
                    ))}
                </ul>
            )}
        </ModalShell>
    );
}

function MarLineRow({
    admissionId,
    line,
}: {
    admissionId: string;
    line: MarRequestLine;
}) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<MedicationAdministrationStatus>('given');
    const [doseText, setDoseText] = useState('');
    const [note, setNote] = useState('');

    const mut = useMutation({
        mutationFn: () =>
            admissionsApi.administer(admissionId, {
                medication_request_id: line.medication_request_id,
                status,
                dose_text: doseText.trim() === '' ? null : doseText.trim(),
                note: note.trim() === '' ? null : note.trim(),
            }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ['admissions', admissionId, 'mar'] });
            setOpen(false);
            setDoseText('');
            setNote('');
            setStatus('given');
        },
    });

    return (
        <li className="rounded-md border border-slate-200 bg-white p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-slate-900 text-sm font-semibold">
                        {line.medication_name}
                    </div>
                    <div className="text-slate-600 text-xs mt-0.5">
                        {[line.dose, line.route, line.frequency].filter(Boolean).join(' · ') || '—'}
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5">
                        {line.status} · prescribed {new Date(line.prescribed_at).toLocaleString()}
                    </div>
                </div>
                {!open && (
                    <button
                        type="button"
                        className="pm-btn pm-btn-sm"
                        onClick={() => setOpen(true)}
                    >
                        Record dose
                    </button>
                )}
            </div>

            {open && (
                <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
                    <div className="flex flex-wrap gap-2">
                        {STATUSES.map((s) => (
                            <button
                                key={s}
                                type="button"
                                className={`pm-btn-sm ${status === s ? 'pm-btn-primary' : 'pm-btn'}`}
                                onClick={() => setStatus(s)}
                                disabled={mut.isPending}
                            >
                                {MEDICATION_ADMINISTRATION_STATUS_LABELS[s]}
                            </button>
                        ))}
                    </div>
                    <input
                        className="pm-input"
                        type="text"
                        placeholder="Dose actually given (e.g. 500 mg)"
                        value={doseText}
                        onChange={(e) => setDoseText(e.target.value)}
                        disabled={mut.isPending}
                        maxLength={160}
                    />
                    <textarea
                        className="pm-input"
                        rows={2}
                        placeholder="Notes (optional)"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={mut.isPending}
                        maxLength={2000}
                    />
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            className="pm-btn-ghost pm-btn-sm"
                            onClick={() => { setOpen(false); setDoseText(''); setNote(''); setStatus('given'); }}
                            disabled={mut.isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="pm-btn-primary pm-btn-sm"
                            onClick={() => mut.mutate()}
                            disabled={mut.isPending}
                        >
                            {mut.isPending ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                    {mut.isError && (
                        <div className="pm-error text-xs">
                            {mut.error instanceof ApiError ? mut.error.message : 'Save failed.'}
                        </div>
                    )}
                </div>
            )}

            {line.administrations.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2">
                    <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">
                        History
                    </div>
                    <ul className="space-y-1 text-xs text-slate-700">
                        {line.administrations.map((a) => (
                            <li key={a.id}>
                                <span className="font-medium">
                                    {MEDICATION_ADMINISTRATION_STATUS_LABELS[a.status]}
                                </span>{' '}
                                · {new Date(a.administered_at).toLocaleString()}
                                {a.dose_text ? ` · ${a.dose_text}` : ''}
                                {a.administered_by_full_name ? ` · by ${a.administered_by_full_name}` : ''}
                                {a.note && <div className="text-slate-500 ml-4 italic">{a.note}</div>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
}
